import React from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Link from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import PieChart from '@amzn/awsui-components-console/pie-chart';
import Spinner from '@amzn/awsui-components-console/spinner';
import type { ContentStats } from '../../types';
import type { Collection } from '@amzn/aws-gcr-content-connection-management-client';
import './DataSourceOverview.scss';

export interface DataSourceOverviewProps {
  stats?: ContentStats;
  collections: Collection[];
  loading: boolean;
}

interface ChartDataItem {
  title: string;
  value: number;
  percentage: number;
  color: string;
}

const DataSourceOverview: React.FC<DataSourceOverviewProps> = ({ stats, collections, loading }) => {
  // Generate chart data from real collections data
  const chartData: ChartDataItem[] = React.useMemo(() => {
    if (!collections.length) return [];

    // Group collections by source and calculate totals
    const sourceMap = new Map<string, { count: number; items: number }>();

    collections.forEach((collection) => {
      const source = collection.source || 'Unknown';
      const existing = sourceMap.get(source);
      if (existing) {
        existing.count++;
        existing.items += collection.itemCount || 0;
      } else {
        sourceMap.set(source, { count: 1, items: collection.itemCount || 0 });
      }
    });

    const totalItems = stats?.totalItems || 0;
    const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'];

    return Array.from(sourceMap.entries()).map(([source, data], index) => ({
      title: source,
      value: data.items,
      percentage: totalItems > 0 ? Math.round((data.items / totalItems) * 100) : 0,
      color: colors[index % colors.length],
    }));
  }, [collections, stats]);

  return (
    <div className="data-source-overview-wrapper">
      <Container>
        <Header variant="h2">Data Sources</Header>
        <SpaceBetween size="l">
          <Box variant="small" color="text-body-secondary">
            <p>
              {loading ? (
                <>
                  <Spinner size="normal" /> Loading data sources...
                </>
              ) : (
                <>
                  With a total of {stats?.totalItems.toLocaleString() || '0'} content items across {stats?.totalSources || 0} data sources.{' '}
                  <Link variant="info" href="#">
                    View All
                  </Link>
                </>
              )}
            </p>
          </Box>

          <Box textAlign="center">
            {loading ? (
              <Box padding="xxl">
                <Spinner size="large" /> Loading chart...
              </Box>
            ) : (
              <PieChart
                data={chartData}
                visibleSegments={chartData}
                segmentDescription={(datum: ChartDataItem) => `${datum.value.toLocaleString()} items, ${datum.percentage}%`}
                detailPopoverContent={(datum: ChartDataItem) => [
                  { key: 'Items', value: datum.value.toLocaleString() },
                  { key: 'Percentage', value: `${datum.percentage}%` },
                ]}
                ariaDescription="Donut chart showing data source distribution."
                ariaLabel="Data Sources Donut Chart"
                innerMetricDescription="Content Items"
                innerMetricValue={stats?.totalItems.toLocaleString() || '0'}
                size="large"
                variant="donut"
                hideFilter
                i18nStrings={{
                  detailsValue: 'Value',
                  detailsPercentage: 'Percentage',
                  filterLabel: 'Filter displayed data',
                  filterPlaceholder: 'Filter data',
                  filterSelectedAriaLabel: 'selected',
                  detailPopoverDismissAriaLabel: 'Dismiss',
                  legendAriaLabel: 'Legend',
                  chartAriaRoleDescription: 'pie chart',
                  segmentAriaRoleDescription: 'segment',
                }}
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No data available</b>
                    <Box variant="p" color="inherit">
                      There is no data available
                    </Box>
                  </Box>
                }
              />
            )}
          </Box>
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default DataSourceOverview;
